import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useSession } from "@/context/SessionContext";
import { useFetch } from "@/lib/useFetch";
import { fmtDate } from "@/lib/format";
import { PageHeader, ErrorState, LoadingRows, NoAccess, EmptyState, Badge } from "@/components/common/States";
import { DueBadge, Pick, Pager, Table, label } from "./shared";

const CATS = [["auto_rickshaw", "Auto"], ["passenger_car", "Car"], ["cargo", "Cargo"], ["two_wheeler", "Two-wheeler"], ["bus", "Bus"], ["other", "Other"]];
const DUE_OPTS = [["in_service", "In service"], ["due", "Due or overdue"], ["overdue", "Overdue"], ["upcoming", "Due soon"], ["scheduled", "Scheduled"], ["none", "Not scheduled"]];
const opts = (a) => a.map(([value, l]) => ({ value, label: l }));

export default function GarageVehicles() {
  const { can, branches } = useSession();
  const nav = useNavigate();
  const [f, setF] = useState({ q: "", branch_id: "", category: "", due: "" });
  const [page, setPage] = useState(1);
  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setPage(1); };
  const params = { page, limit: 25, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) };
  const { data, loading, error, reload } = useFetch("/garage/vehicles", params, can("vehicle.view"));
  if (!can("vehicle.view")) return <NoAccess perm="vehicle.view" />;
  const cols = [
    ["Registration", (r) => <span className="font-mono-g font-semibold">{r.registration_number}</span>],
    ["Model", (r) => <span>{r.make} {r.model} <span className="text-muted-foreground text-xs">· {CATS.find((c) => c[0] === r.category)?.[1] || label(r.category)}</span></span>],
    ["Branch", (r) => r.branch_name || "—"],
    ["Driver", (r) => r.driver_name || <span className="text-muted-foreground">Unassigned</span>],
    ["Odometer", (r) => `${Number(r.odometer).toLocaleString("en-IN")} km`, "text-right whitespace-nowrap"],
    ["Last service", (r) => (r.last_service ? fmtDate(r.last_service) : "—"), "whitespace-nowrap"],
    ["Next service", (r) => <div className="flex flex-wrap items-center gap-1.5">{r.in_service && <Badge tone="gold">In service</Badge>}<DueBadge s={r.due_state} testId={`vehicle-due-${r.id}`} /></div>],
  ];
  return (
    <div data-testid="garage-vehicles-page">
      <PageHeader title="Vehicles" subtitle="Tap a vehicle to see its service history."
        actions={can("vehicle.manage") && <><Button onClick={() => nav("/vehicles?new=1")} data-testid="add-vehicle-btn">Add vehicle</Button><Button variant="outline" onClick={() => nav("/vehicles")} data-testid="manage-vehicle-records-btn">Edit records</Button></>} />
      <div className="g39-card">
        <div className="flex flex-col sm:flex-row flex-wrap gap-3 p-4 border-b bg-secondary/40">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9 bg-white" placeholder="Search registration, make or model" value={f.q} onChange={(e) => set("q", e.target.value)} data-testid="vehicles-search-input" />
          </div>
          {branches.length > 1 && <Pick value={f.branch_id} onChange={(v) => set("branch_id", v)} allLabel="All branches" options={branches.map((b) => ({ value: b.id, label: b.name }))} testId="vehicles-branch-filter" />}
          <Pick value={f.category} onChange={(v) => set("category", v)} allLabel="All types" options={opts(CATS)} testId="vehicles-category-filter" />
          <Pick value={f.due} onChange={(v) => set("due", v)} allLabel="Any service status" options={opts(DUE_OPTS)} testId="vehicles-due-filter" />
        </div>
        {loading && <LoadingRows rows={5} />}
        {error && <ErrorState message={error} onRetry={reload} />}
        {data && !data.items.length && <EmptyState title="No vehicles match" body="Change the filters, or add vehicles from the vehicle records screen." testId="vehicles-empty" />}
        {data && data.items.length > 0 && <Table cols={cols} rows={data.items} testId="vehicles-table" onRow={(r) => nav(`/service-timeline/${r.id}`)} />}
        {data && <Pager page={page} total={data.total} limit={25} onPage={setPage} testId="vehicles-pager" />}
      </div>
    </div>
  );
}
